import 'core-js/stable'
import 'regenerator-runtime/runtime'

import api from './api'
import { cmp_units, cmp_uids } from './util'
import Cookies from 'js-cookie'
import moment from 'moment'
import Vue from 'vue'
import Vuex from 'vuex'

const FETCH_PERIOD_1 = 5  // don't fetch any particular thing more than once in 5 seconds
const FETCH_PERIOD_2 = 60 // fetch anything not fetched within the last 60 seconds

Vue.use(Vuex)

const debug = process.env.NODE_ENV !== 'production'

let fref = null,
  uref = null

let store = new Vuex.Store({
  state: {
    activeMenu: 'main',
    menuVisible: false,
    listening: false,
    user: 0,
    permissions: null,
    time: null,
    units: {},
    lastPermissionsFetched: null,
    filterUnitName: '',
    filterUnitNameLC: '',
    filterFleetId: null,
    poke: null, // use me to wake up my listener
    selectedUnit: null,
    selectedPoi: null,
    hoverUnitId: null,
    hoverPoi: null,
    mousePosition: [0,0],
    poiBounds: {},
    uids_sorted_by_uid: [],
    uids_sorted_by_name: [],
    unreadCount: 0,           // # of unread advisories
    owner: {
      fleetIds: null,
      users: null,
      sortedUsers: null,
      fleetIdsLoading: false,
      usersLoading: false,
    },
    showInvitations: false,
    timezones: null,
    timezonesRef: null,
    billingAccounts: null,
    billingAccountsRef: null,
  },
  getters: {
    unconfirmedFleets(s) {
      let ff = []
      if (s.permissions)
        for (const f of s.permissions.fleets)
          if (f.unconfirmed)
            ff.push(f)
      return ff
    },
    fleets(s) {
      let ff = {}
      if (s.permissions)
        for (const f of s.permissions.fleets)
          if (!f.unconfirmed)
            ff[f.id] = f
      return ff
    },
    pois(s,g) {
      let pp = {}
      for (const k in g.fleets)
        for (const p of g.fleets[k].pois)
          pp[p.id] = p
      return pp
    },
    myManagedFleets(s, g) {
      let ids = []
      for (let k in g.fleets)
        if (g.fleets[k].managed || (s.user && s.user.is_superuser))
          ids.push(k)
      return ids
    },
    unit_fleet(s, g) {
      let rv = {}
      for (const k in g.fleets) {
        const f = g.fleets[k]
        for (const uid of f.units) {
          rv[uid] = f
        }
      }
      return rv
    },
    is_uid_visible: (s,g) => (uid) => {
      let u = s.units[uid]
      if (u === null) return false
      if (s.filterFleetId !== null && s.filterFleetId !== g.unit_fleet[uid].id) return false
      if (s.filterUnitNameLC && u.name.toLowerCase().indexOf(s.filterUnitNameLC) < 0) return false
      return true
    },
    is_pid_visible: (s,g) => (pid) => {
      let p = g.pois[pid]
      if (p === null) return false
      if (s.filterFleetId !== null && s.filterFleetId !== p.fleetId) return false
      return true
    },
    unitStatus: (s,g) => (unit) => {
      if (!unit) return     // no unit object; skip
      if (!unit.online) {
        return {
          color: '#aaa',    // GRAY for offline
          text: 'Offline',
        }
      } else if (!unit.lastPacketTime || s.time.diff(unit.lastPacketTime, 'minutes') > unit.hb_interval + 10) {
        return {
          color: '#f00',    // RED for logging error
          text: 'Heartbeat Error',
        }
      } else if (unit.gps.length && unit.gps[0].time.isSame(unit.lastPacketTime)) {
        return {
          color: g.unit_fleet[unit.id].unit_color,  // GREEN (or other color selected by manager) for GPS OK
          text: 'Online',
        }
      } else {
        return {
          color: '#ac0',   // YELLOW for GPS stale
          text: unit.gps.length ? 'Stale GPS data' : 'No GPS data',
        }
      }
    },
  },
  mutations: {
    selectMenu(s, menu) {
      s.activeMenu = menu
    },
    hideMenu(s) {
      s.menuVisible = false
    },
    showMenu(s) {
      s.menuVisible = true
    },
    setUser(s, user) {
      s.user = user
      localStorage.setItem('user', JSON.stringify(user))
      s.units = {}
      s.uids_sorted_by_uid = []
      s.uids_sorted_by_name = []
      s.lastPermissionsFetched = null
    },
    hideInvitations(s) {
      s.showInvitations = false
      let fids = []
      for (const f of s.permissions.fleets)
        if (f.unconfirmed)
          fids.push(f.id)
      Cookies.set('ignoredInvitations', fids.join(','))
    },
    setPermissions(s, permissions) {

      // Cache old POI objects:
      let oldPois = {}
      for (const f of (s.permissions && s.permissions.fleets || []))
        if (!f.unconfirmed)
          for (const p of f.pois)
            oldPois[p.id] = p

      let ignoredInvitations = Cookies.get('ignoredInvitations')
      ignoredInvitations = new Set(ignoredInvitations ? ignoredInvitations.split(',') : [])
      let newIgnored = []

      for (const f of (permissions && permissions.fleets || [])) {

        const sfid = f.id.toString()
        if (f.unconfirmed) {
          if (!ignoredInvitations.has(sfid)) {
            console.log('new invitation', sfid, typeof(sfid), ignoredInvitations)
            s.showInvitations = true
          }
          newIgnored.push(sfid)
          continue
        }

        // If POI is in cache, update old object with new values, then replace the new POI with the old object.
        for (let i=0; i<f.pois.length; i++) {
          const np = f.pois[i]
          np.fleetId = f.id
          const op = oldPois[np.id]
          if (op) {
            for (const k in np)
              Vue.set(op, k, np[k])
            f.pois[i] = op
          }
        }

        // Sort the unit IDs.
        let units = f.units.slice()
        units.sort()

        // Make sure all the uids are in the database.
        let i = 0
        for (const uid of units) {
          if (!(uid in s.units)) {

            // Update uids_sorted_by_uid:
            while (uid < s.uids_sorted_by_uid[i])
              i++
            s.uids_sorted_by_uid.splice(i, 0, uid)

            // Add the uid to the dictionary:
            Vue.set(s.units, uid, null)

          }
        }

      }

      if (permissions !== null)
        Cookies.set('ignoredInvitations', newIgnored.join(','))

      // Apply the adjusted permissions object to the application state.
      s.permissions = permissions

      // Save the permissions to the browser for fast refresh/reload/return.
      localStorage.setItem('permissions', JSON.stringify(permissions))

      // Note when the permissions were last fetched.
      s.lastPermissionsFetched = moment()

    },
    updatePoi(s, { poi, fleet }) {
      for (const p of fleet.pois)
        if (p.id === poi.id) {
          // POI already existed; copy new values
          for (const key in poi) {
            p[key] = JSON.parse(JSON.stringify(poi[key]))
          }
          return
        }
      // POI is new; add it
      fleet.pois.push(JSON.parse(JSON.stringify(poi)))
    },
    deletePoi(s, { poi, fleet }) {
      for (let i=0; i<fleet.pois.length; i++) {
        let p = fleet.pois[i]
        if (p.id === poi.id) {
          fleet.pois.splice(i, 1)
          return
        }
      }
    },
    updateUnit(s, { uid, unit }) {
      let i, oldUnit

      if (unit) {
        oldUnit = s.units[uid]

        // Tweak unit data fields:
        unit.lastFetched = moment()

        // Add unit to the database:
        Vue.set(s.units, uid, unit)

        // Has the unit's sorted order changed?
        if (!oldUnit || 0 != cmp_units(oldUnit, unit)) {

          // Remove the unit from its old sorted position (if already in the list):
          i = s.uids_sorted_by_name.indexOf(uid)
          if (i >= 0) {
            s.uids_sorted_by_name.splice(i, 1)
          }

          // Find the unit's new sorted position:
          let left = 0, right = s.uids_sorted_by_name.length
          while (left < right) {
            i = Math.floor((left + right) / 2)
            if (cmp_uids(s, uid, s.uids_sorted_by_name[i]) < 0)
              right = i
            else
              left = i + 1
          }

          // Insert the unit at its sorted position:
          s.uids_sorted_by_name.splice(left, 0, uid)
        }

      } else {

        // Remove unit from sorted_by_uid list:
        s.uids_sorted_by_uid.splice(s.uids_sorted_by_uid.indexOf(uid), 1)

        // Remove unit from sorted_by_name list (if already in the list):
        i = s.uids_sorted_by_name.indexOf(uid)
        if (i >= 0) s.uids_sorted_by_name.splice(i, 1)

        // Delete unit from database:
        Vue.delete(s.units, uid)

      }
    },
    updatePoiBounds(s, { pid, bounds }) {
      Vue.set(s.poiBounds, pid, bounds)
    },
    updateTime(s, time) {
      s.time = time
    },
    setFilterUnitName(s, filterUnitName) {
      s.filterUnitName = filterUnitName || ''
      s.filterUnitNameLC = s.filterUnitName.toLowerCase()
    },
    setFilterFleetId(s, filterFleetId) {
      s.filterFleetId = filterFleetId
    },
    setListening(s, listening) {
      s.listening = listening
    },
    setPoke(s, poke) {
      s.poke = poke
    },
    selectUnit(s, uid) {
      s.selectedUnit = uid
      if (uid !== null)
        s.selectedPoi = null
    },
    hoverUnitId(s, uid) {
      s.hoverUnitId = uid
    },
    selectPoi(s, pid) {
      s.selectedPoi = pid
      if (pid !== null)
        s.selectedUnit = null
    },
    hoverPoi(s, poi) {
      s.hoverPoi = poi
    },
    mousemove(s, pos) {
      s.mousePosition = pos
    },
    updateFleet(s, { fleet, flds }) {
      for (const key in flds) {
        fleet[key] = flds[key]
      }
      // No need to update local storage. It will get updated soon anway.
      //localStorage.setItem('permissions', JSON.stringify(permissions))
    },
    setUnreadCount(s, count) {
      s.unreadCount = count
    },
    setOwnerFleetIds(s, o) {
      if (o && o.fleetIds) {
        s.owner.fleetIds = o.fleetIds
        fref = null
      } else {
        s.owner.fleetIds = null
        fref = (o && o.ref) || null
      }
      s.owner.fleetIdsLoading = (fref !== null)
    },
    setOwnerUsers(s, o) {
      if (o && o.users) {
        s.owner.users = o.users
        s.owner.sortedUsers = o.sortedUsers
        uref = null
      } else {
        s.owner.users = null
        s.owner.sortedUsers = null
        uref = (o && o.ref) || null
      }
      s.owner.usersLoading = (uref !== null)
    },
    changeUserNames(s, o) {
      s.user.firstName = o.firstName
      s.user.lastName = o.lastName
    },
    invitationAccepted(s, fid) {
      for (const f of s.permissions.fleets) {
        if (f.id !== fid)
          continue
        f.unconfirmed = false
        Vue.set(f, 'subscriber', true)
        break
      }
    },
    subscriberSet(s, { fid, subscriber }) {
      for (const f of s.permissions.fleets) {
        if (f.id !== fid)
          continue
        Vue.set(f, 'subscriber', subscriber)
        break
      }
    },
    accessDeleted(s, fid) {
      const ff = s.permissions.fleets
      for (let i=0; i<ff.length; i++) {
        if (ff[i].id !== fid)
          continue
        ff.splice(i, 1)
        break
      }
    },
    refreshPermissions(s) {
      s.lastPermissionsFetched = null
    },
    updateFMSSeq(s, [ fleet, fms_seq ]) {
      fleet.fms_seq = fms_seq
    },
    timezones(s, o) {
      if ('ref' in o) s.timezonesRef = o.ref
      if ('timezones' in o) s.timezones = o.timezones
    },
    billingAccounts(s, o) {
      if ('ref' in o) s.billingAccountsRef = o.ref
      if ('billingAccounts' in o) s.billingAccounts = o.billingAccounts
    },
  },
  actions: {
    async logout() {
      document.getElementById('logoutForm').submit()
    },
    async getUser({ commit, dispatch }) {
      let u = await api.getUser()
      commit('setPermissions', null)
      commit('setUnreadCount', 0)
      commit('setUser', u)
      dispatch('poke')
    },
    async getPermissions({ commit }) {
      let pp = await api.getPermissions()
      commit('setPermissions', pp) 
      commit('setUnreadCount', pp && pp.unreadCount || 0)
    },
    async getUnits({ state, getters, commit }) {
      let uids = state.uids_sorted_by_uid.slice(),
        now = moment()
      uids.sort((a, b) => {
        a = state.units[a]
        b = state.units[b]
        return (!a
          ? (!b ? 0 : -1)
          : (!b ? 1 : a.lastFetched.diff(b.lastFetched)))
      })
      let fetchList = []
      if (state.selectedUnit !== null) {
        let a = state.units[state.selectedUnit]
        if (!a || now.diff(a.lastFetched, 'seconds') > FETCH_PERIOD_1)
          fetchList.push(state.selectedUnit)
      }
      let i
      for (i = 0; i < uids.length && fetchList.length < 10; i++) {
        if (uids[i] === state.selectedUnit)
          continue
        let u = state.units[uids[i]]
        if (u && now.diff(u.lastFetched, 'seconds') <= FETCH_PERIOD_1)
          break
        fetchList.push(uids[i])
      }
      let o = await api.getUnits({ uids: fetchList })
      commit('updateTime', o.until)
      for (const uid of fetchList) {
        commit('updateUnit', {
          uid,
          unit: o.units[uid],
        })
      }

      // Did we fetch everything in one chunk? If so, delay before polling again (return false).
      if (i >= uids.length)
        return false

      // Poll immediately iff the next unit has never been fetched or was fetched >60s ago.
      let u = state.units[uids[i]]
      return !u || now.diff(u.lastFetched, 'seconds') > FETCH_PERIOD_2

    },
    async listen({ state, getters, commit, dispatch }) {
      commit('setListening', 'connecting')
      try {
        let errCount = 0
        for (;;) {
          try {
            if (!(await dispatch('poll'))) {
              // falsish poll means: wait for a bit before polling again
              commit('setListening', true)
              await new Promise((y,n) => {
                let poke = (timeout) => {
                  if (y === null) {
                    return
                  }
                  if (!timeout) {
                    commit('setListening', 'connecting')
                  }
                  commit('setPoke', null)
                  let cb = y
                  y = null
                  cb()
                }
                commit('setPoke', poke)
                setTimeout(() => { poke(true) }, 5000)
              })
            } else {
              // truish poll means: poll again immediately
              commit('setListening', 'connecting')
            }
          } catch(e) {
            if (errCount >= 3)
              throw e
            console.error(e)
            errCount++
          }
        }
      } finally {
        // no longer listening!
        commit('setListening', false)
      }
    },
    async poll({ state, getters, dispatch }) {
      if (state.user === 0) {
        await dispatch('getUser')
      }
      if (state.user === null) {
        return false // no user, nothing to poll for
      }
      if (state.lastPermissionsFetched === null || moment().diff(state.lastPermissionsFetched, 'seconds') > 60) {
        await dispatch('getPermissions')
        return !!state.uids_sorted_by_uid.length // fetch again immediately if we have access to any units
      } else {
        return await dispatch('getUnits')
      }
    },
    async poke({ state }) {
      if (state.poke)
        state.poke()
    },
    async savePoi({ commit }, { poi, fleet }) {
      if ('id' in poi) {
        await api.savePoi({ poi })
      } else {
        Vue.set(poi, 'id', await api.savePoi({ poi, fleet }))
      }
      commit('updatePoi', { poi, fleet })
    },
    async deletePoi({ commit }, { poi, fleet }) {
      await api.deletePoi({ poi })
      commit('deletePoi', { poi, fleet })
    },
    async updateFleet({ state, getters, commit }, ref) {
      let { fid, flds } = ref
      await api.updateFleet(ref)
      commit('updateFleet', { fleet:getters.fleets[fid], flds })
    },
    async refreshOwnerFleetIds({ state, getters, commit }, o) {
      if (o && o.weak && (state.owner.fleetIds !== null || fref !== null))
        return
      if (fref !== null)
        fref.cancel()
      let ref = {},
        p = api.getOwnerAccounts(ref)
      commit('setOwnerFleetIds', { ref })
      try {

        // Fetch accounts owned by this user.
        let accounts = new Set(await p)
        if (ref !== fref)
          return

        // Get fleets on these accounts in sorted order.
        let ff = getters.fleets,
          fleetIds = Object.keys(ff).filter(k => accounts.has(ff[k].account_id))
        fleetIds.sort((a,b) => ((ff[a].name < ff[b].name) ? -1 : ((ff[a].name > ff[b].name) ? 1 : 0)))

        // Update the state.
        commit('setOwnerFleetIds', { fleetIds })

      } catch(e) {

        // Ignore error if request is obsolete.
        if (ref !== fref)
          return

        // Log the error.
        console.error(e)

        // Update the state.
        commit('setOwnerFleetIds', null)

      }
    },
    async refreshOwnerUsers({ state, getters, commit }, o) {
      if (o && o.weak && (state.owner.users !== null || uref !== null))
        return
      if (uref !== null)
        uref.cancel()
      let ref = {},
        p = api.getOwnerUsers(ref)
      commit('setOwnerUsers', { ref })
      try {
      
        // Fetch users associated with accounts owned by this user.
        let uu = await p
        if (ref !== uref)
          return

        let users = {}
        for (let i=0; i<uu.length; i++) {
          let u = uu[i]
          u = (
            typeof(u) === 'string'
              ? { email: u.substring(1), needsInvitation: (u[0] === '1') }
              : { email: u[0], firstName: u[1], lastName: u[2] })
          users[u.email] = u
          uu[i] = u
        }

        // Update the state.
        commit('setOwnerUsers', { users, sortedUsers: uu })

      } catch(e) {

        // Ignore error if request is obsolete.
        if (ref !== uref)
          return

        // Log the error.
        console.error(e)

        // Update the state.
        commit('setOwnerUsers', null)

      }
    },
    async changeUserNames({ commit }, { firstName, lastName }) {
      await api.changeUserNames({ firstName, lastName })
      commit('changeUserNames', { firstName, lastName })
    },
    async acceptInvitation({ commit, dispatch }, fid) {
      await api.acceptInvitation({ fid })
      commit('invitationAccepted', fid)
      dispatch('refreshPermissions')
    },
    async setSubscriber({ commit }, { fid, subscriber }) {
      await api.setSubscriber({ fid, subscriber })
      commit('subscriberSet', { fid, subscriber })
    },
    async deleteAccess({ commit }, fid) {
      await api.deleteAccess({ fid })
      commit('accessDeleted', fid)
    },
    refreshPermissions({ commit, dispatch }) {
      commit('refreshPermissions')
      dispatch('poke')
    },
    fetchTimezones({ state, commit }) {
      if (state.timezones) return
      if (state.timezonesRef) return
      const ref = {}
      commit('timezones', { ref, })
      api.getTimezones(ref).then(
        (timezones) => { commit('timezones', { timezones, ref: null }) }
      ).catch(
        () => { commit('timezones', { ref: null }) }
      )
    },
    fetchBillingAccounts({ state, commit }, force) {
      if (state.billingAccountsRef) return
      if (state.billingAccounts && !force) return
      const ref = {}
      commit('billingAccounts', { ref: ref, billingAccounts: null })
      api.getBillingAccounts(ref).then(
        (billingAccounts) => { commit('billingAccounts', { billingAccounts, ref: null }) }
      ).catch(
        () => { commit('billingAccounts', { ref: null }) }
      )
    },
  },
  strict: debug,
})
global.store = store
global.api = api

export default store
